import React, { useEffect, useState } from "react";
import axiosInstance from "../redux/helpers/axios";

/**
 * @author
 * @function DeliveryRequests
 **/

const STATUSES = [
  "DELIVERY_REQUESTED",
  "DELIVERY_PROCESSING",
  "READY_FOR_DELIVERY",
  "HANDOVER_TO_COURIER",
  "DELIVERED",
];

const formatStatus = (status) =>
  status
    ?.split("_")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");

export const DeliveryRequests = (props) => {
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");

  // items modal
  const [itemsModalRequest, setItemsModalRequest] = useState(null);

  // status update modal
  const [statusModalRequest, setStatusModalRequest] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [newTrackingNo, setNewTrackingNo] = useState("");
  const [newCodAmount, setNewCodAmount] = useState("");
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  const fetchDeliveryRequests = async () => {
    try {
      const res = await axiosInstance.get(
        "/delivery/services/get-all-requests",
      );
      setRequests(res.data.data);
    } catch (err) {
      console.error("Error fetching delivery requests:", err);
    }
  };

  useEffect(() => {
    fetchDeliveryRequests();
  }, []);

  const openItemsModal = (req) => setItemsModalRequest(req);
  const closeItemsModal = () => setItemsModalRequest(null);

  const openStatusModal = (req) => {
    setStatusModalRequest(req);
    setNewStatus(req.status || "");
    setNewTrackingNo(req.trackingNo || "");
    setNewCodAmount(req.codAmount ?? "");
    setUpdateError(null);
  };
  const closeStatusModal = () => {
    setStatusModalRequest(null);
    setNewStatus("");
    setNewTrackingNo("");
    setNewCodAmount("");
    setUpdateError(null);
  };

  const submitStatusUpdate = async () => {
    if (!statusModalRequest || !newStatus) return;
    setUpdating(true);
    setUpdateError(null);
    try {
      // NOTE: adjust to your actual update-status route/method if this differs
      await axiosInstance.put(
        `/delivery/services/update-request-status/${statusModalRequest.requestId}`,
        {
          status: newStatus,
          trackingNo: newTrackingNo,
          codAmount: newCodAmount === "" ? undefined : Number(newCodAmount),
        },
      );
      await fetchDeliveryRequests();
      closeStatusModal();
    } catch (err) {
      console.error("Error updating delivery request status:", err);
      setUpdateError(
        err.response?.data?.msg || "Failed to update status. Please try again.",
      );
    } finally {
      setUpdating(false);
    }
  };

  function getAliCdnUrl(url) {
    const filename = url.split("/").pop().split("?")[0];
    return `https://img.alicdn.com/${filename}`;
  }

  const filteredRequests = requests
    .filter((req) => statusFilter === "ALL" || req.status === statusFilter)
    .filter((req) =>
      searchQuery.trim()
        ? String(req.requestId)
            .toLowerCase()
            .includes(searchQuery.trim().toLowerCase())
        : true,
    );

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <label
          htmlFor="status-filter"
          className="text-sm font-medium text-gray-700"
        >
          Status:
        </label>
        <select
          id="status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700"
        >
          <option value="ALL">All</option>
          {STATUSES.map((status) => (
            <option key={status} value={status}>
              {formatStatus(status)}
            </option>
          ))}
        </select>

        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by Request ID"
          className="ml-auto w-56 rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700"
        />
      </div>

      <div className="space-y-4">
        {filteredRequests.map((req) => (
          <div
            key={req.id}
            className="overflow-hidden rounded-lg border border-gray-200"
          >
            <div className="flex items-center justify-between bg-gray-50 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-gray-800">
                  Request #{req.requestId}
                </p>

                <p className="mt-0.5 text-xs text-gray-500">
                  {req.courier}
                  {req.trackingNo && (
                    <span> · Tracking: {req.trackingNo}</span>
                  )}{" "}
                  · {Number(req.totalWeight).toFixed(2)} kg
                </p>
                {req.user?.name && (
                  <p className="mt-0.5 text-xs text-gray-500">
                    {req.user.name} -{" "}
                    {req.address?.phone || req.user.phone || "No phone"}
                  </p>
                )}
                <p className="mt-0.5 text-xs text-gray-500">
                  {req.address?.city && (
                    <span>
                      {req.address.street ? `${req.address.street}, ` : ""}
                      {req.address.city}
                    </span>
                  )}{" "}
                </p>
                {req.status && (
                  <span className="mt-1 inline-block rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
                    {formatStatus(req.status)}
                  </span>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2 text-right">
                <button
                  type="button"
                  onClick={() => openItemsModal(req)}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                >
                  View Items
                </button>
                <button
                  type="button"
                  onClick={() => openStatusModal(req)}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                >
                  Update Status
                </button>
                <button
                  type="button"
                  onClick={() =>
                    window.open(
                      `https://core.alistore.com.bd/api/v1/delivery/services/print-label?requestId=${req.requestId}`,
                      "_blank",
                    )
                  }
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                >
                  Print Label
                </button>
              </div>
            </div>
          </div>
        ))}

        {filteredRequests.length === 0 && (
          <p className="py-8 text-center text-sm text-gray-500">
            No delivery requests found.
          </p>
        )}
      </div>

      {itemsModalRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="max-h-[85vh] w-full max-w-lg overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <p className="text-sm font-semibold text-gray-800">
                Items · Request #{itemsModalRequest.requestId}
              </p>
              <button
                type="button"
                onClick={closeItemsModal}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="max-h-[70vh] divide-y divide-gray-100 overflow-y-auto">
              {itemsModalRequest.orderItems?.length > 0 ? (
                itemsModalRequest.orderItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 px-4 py-2.5"
                  >
                    {item.store === "A1688" ? (
                      <img
                        src={getAliCdnUrl(item.productImg)}
                        alt={item.productTitle}
                        className="w-24 h-24 shrink-0 rounded object-cover"
                      />
                    ) : (
                      <img
                        src={item.productImg}
                        alt={item.productTitle}
                        className="w-24 h-24 shrink-0 rounded object-cover"
                      />
                    )}

                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800 line-clamp-1">
                        {item.productTitle}
                      </p>
                      <p className="text-sm font-medium text-gray-800 line-clamp-1">
                        Item <strong>#{item.itemId}</strong>
                      </p>
                      {item.shipment?.category && (
                        <p className="text-sm font-medium text-gray-800 line-clamp-1">
                          Category: <strong>{item.shipment.category}</strong>
                        </p>
                      )}
                      <p className="mt-0.5 text-xs text-gray-500">
                        Total Qty: <strong>{item.totalQty}</strong>
                      </p>

                      {item.shipment?.totalWeight != null && (
                        <p className="mt-0.5 text-xs text-gray-500">
                          Weight:{" "}
                          <strong>{item.shipment.totalWeight} KG</strong>
                        </p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="px-4 py-6 text-center text-sm text-gray-500">
                  No items in this request.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {statusModalRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <p className="text-sm font-semibold text-gray-800">
                Update Status · Request #{statusModalRequest.requestId}
              </p>
              <button
                type="button"
                onClick={closeStatusModal}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 px-4 py-4">
              <div>
                <label
                  htmlFor="status-update-select"
                  className="mb-1 block text-xs font-medium text-gray-700"
                >
                  Status
                </label>
                <select
                  id="status-update-select"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-700"
                >
                  {STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {formatStatus(status)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="tracking-no-input"
                  className="mb-1 block text-xs font-medium text-gray-700"
                >
                  Tracking No. (optional)
                </label>
                <input
                  id="tracking-no-input"
                  type="text"
                  value={newTrackingNo}
                  onChange={(e) => setNewTrackingNo(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-700"
                  placeholder="Enter tracking number"
                />
              </div>

              <div>
                <label
                  htmlFor="cod-amount-input"
                  className="mb-1 block text-xs font-medium text-gray-700"
                >
                  COD Amount
                </label>
                <input
                  id="cod-amount-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={newCodAmount}
                  onChange={(e) => setNewCodAmount(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-700"
                  placeholder="Enter COD amount"
                />
              </div>

              {updateError && (
                <p className="text-xs text-red-600">{updateError}</p>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
              <button
                type="button"
                onClick={closeStatusModal}
                disabled={updating}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitStatusUpdate}
                disabled={updating || !newStatus}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {updating ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
